import {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Redirect, Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import i18n, {loadLanguage} from './i18n';
import Menu from './components/Menu.js';
import Home from './pages/Home.js';
import AppPage from './pages/AppPage.js';
import Layout from './components/home/Layout';
import {LangContext} from './hooks/useLang';
import {LANGUAGES, buildPath, detectLanguage, storeLanguage} from './services/language';
import {FAQ_DESCRIPTION, FAQ_TITLE, POLICIES, faqAnswerDescription, faqAnswerTitle} from './data/docPages';
// Old app pages, replaced by AppPage on the same URLs
// import PtMobilePage from './pages/PtMobilePage.js';
// import PtWebPage from './pages/PtWebPage.js';
import PtGamePage from './pages/PtGamePage.js';
// import PhysicsPage from './pages/PhysicsPage.js';
import EseninPage from './pages/EseninPage.js';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.js';
import PrivacyPolicyIosPage from './pages/PrivacyPolicyIosPage.js';
import FaqPage from "./pages/FaqPage";
import FaqAnswerPage from "./pages/FaqAnswerPage";
// import ReviewsPage from "./pages/ReviewsPage";
// import FeedbackPage from "./pages/FeedbackPage";
// import UniverseSayPage from "./pages/UniverseSayPage";
import PhysicsPrivacyPolicyEnPage from "./pages/PhysicsPrivacyPolicyEnPage";
import PhysicsPrivacyPolicyRuPage from "./pages/PhysicsPrivacyPolicyRuPage";

// '/:lang(en|ru|es|...)'
const LANG_ROUTE = `/:lang(${LANGUAGES.join('|')})`;

// Pages in every language live under /en/..., /ru/..., /es/... and so on
const LangRoutes = ({match}) => {
  const {lang} = match.params;
  const [loaded, setLoaded] = useState(() => i18n.hasResourceBundle(lang, 'translation'));

  useEffect(() => {
    let cancelled = false;
    storeLanguage(lang);
    loadLanguage(lang).then(() => {
      i18n.changeLanguage(lang);
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Translations of this language are a separate chunk: render once they are here
  if (!loaded) return null;

  return (
    <LangContext.Provider value={lang}>
      <Switch>
        <Route exact path="/:lang" component={Home}/>
        <Route exact path="/:lang/pt-mobile" render={() => <AppPage key="pt" id="pt"/>}/>
        <Route exact path="/:lang/pt-web" render={() => <AppPage key="pt-web" id="pt" section="web"/>}/>
        <Route exact path="/:lang/physics" render={() => <AppPage key="physics" id="physics"/>}/>
        <Redirect to={buildPath(lang)}/>
      </Switch>
    </LangContext.Provider>
  );
};

// "/" and the old addresses without a language go to the visitor's language
const toDetectedLanguage = ({location}) => (
  <Redirect to={`${buildPath(detectLanguage(), location.pathname)}${location.search}${location.hash}`}/>
);

// FAQ and privacy policies: English-only content (the Russian Physics policy aside),
// no language prefix, rendered inside the same shell as the new pages
const docPage = (Page, seo) => (props) => (
  <Layout
    lang={seo.lang || 'en'}
    seo={{
      localized: false,
      path: props.location.pathname,
      title: seo.getTitle ? seo.getTitle(props) : seo.title,
      description: seo.getDescription ? seo.getDescription(props) : seo.description,
    }}
    className="home--doc"
  >
    <Page/>
  </Layout>
);

const policyRoute = (path, Page) => (
  <Route exact path={path} render={docPage(Page, POLICIES[path])}/>
);

const App = () => {

  return (
    <Router>
      <Switch>
        <Route exact path="/" render={toDetectedLanguage}/>
        <Route exact path={['/pt-mobile', '/pt-web', '/physics']} render={toDetectedLanguage}/>
        <Route path={LANG_ROUTE} component={LangRoutes}/>
        <Route exact path="/faq" render={docPage(FaqPage, {title: FAQ_TITLE, description: FAQ_DESCRIPTION})}/>
        <Route
          exact
          path="/faq/:id"
          render={docPage(FaqAnswerPage, {
            getTitle: ({match}) => faqAnswerTitle(match.params.id),
            getDescription: ({match}) => faqAnswerDescription(match.params.id),
          })}
        />
        {policyRoute('/privacy-policy', PrivacyPolicyPage)}
        {policyRoute('/privacy-policy-ios', PrivacyPolicyIosPage)}
        {policyRoute('/physics/privacy-policy/en', PhysicsPrivacyPolicyEnPage)}
        {policyRoute('/physics/privacy-policy/ru', PhysicsPrivacyPolicyRuPage)}
        <Route>
          <div className="page-wrapper">
            {/* Archived pages in the old design (/pt-game, /esenin): reachable, but kept out of search */}
            <Helmet>
              <meta name="robots" content="noindex"/>
            </Helmet>
            <Menu/>
            <main className="page-main">
              <Route render={({location}) => (
                <TransitionGroup>
                  <CSSTransition key={location.key} timeout={450} classNames="fade">
                    <Switch>
                      {/*<Route exact path="/pt-mobile" render={() => <PtMobilePage/>}/>*/}
                      {/*<Route exact path="/pt-web" render={() => <PtWebPage/>}/>*/}
                      <Route
                        exact
                        path="/pt-game"
                        render={() => (
                          <PtGamePage/>
                        )}
                      />
                      {/*<Route exact path="/universe-say" render={() => (<UniverseSayPage/>)}/>*/}
                      {/*<Route exact path="/physics" render={() => <PhysicsPage/>}/>*/}
                      <Route
                        exact
                        path="/esenin"
                        render={() => (
                          <EseninPage/>
                        )}
                      />
                      {/*<Route*/}
                      {/*  exact*/}
                      {/*  path="/reviews"*/}
                      {/*  render={() => (*/}
                      {/*    <ReviewsPage/>*/}
                      {/*  )}*/}
                      {/*/>*/}
                      {/*<Route*/}
                      {/*  exact*/}
                      {/*  path="/reviews/:id"*/}
                      {/*  render={() => (*/}
                      {/*    <FeedbackPage/>*/}
                      {/*  )}*/}
                      {/*/>*/}
                    </Switch>
                  </CSSTransition>
                </TransitionGroup>
              )}/>
            </main>
          </div>
        </Route>
      </Switch>
    </Router>
  );
};

export default App;
